/**
 * Datacrate brand logo — the horizontal "DA — DATACRATE" wordmark
 * rendered inline as theme-adaptive SVG.
 *
 * Why inline (not <img src="/da-logo-tag.svg">): the app ships both a dark and
 * a light theme (toggled by a `.light` class on <html>, not the OS media query).
 * Inlining lets every glyph follow `currentColor` (pinned to
 * `--color-text-primary`: near-white on dark, near-black on light) so the
 * wordmark reads bright in dark mode and dark in light mode — no recolour, no
 * extra network request, crisp at any DPR. The source `logo.svg` already paints
 * its paths with `fill:currentColor` (a `.cls-1` style class); we inline those
 * paths with `fill="currentColor"` directly and drop the <style> block.
 *
 * viewBox is the artwork's native 201.61 x 32.76 (~6.15:1 — a wide wordmark).
 * Callers size it by height (`style={{ height }}`); width auto-scales so the
 * aspect ratio is never stretched.
 */
import React from 'react';

interface Props {
  /** Rendered height in CSS pixels; width auto-scales to preserve aspect. */
  height?: number;
  /** Accessible name; also drives the title element for screen readers. */
  title?: string;
  className?: string;
}

export function BrandLogo({
  height = 56,
  title = 'Datacrate',
  className,
}: Props): React.JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 252 48"
      role="img"
      aria-label={title}
      style={{ height, width: 'auto' }}
      className={`text-text-primary ${className ?? ''}`}
    >
      <title>{title}</title>
      <style>{`
        .dc-logo-line { stroke: #7c8694; }
        .dc-logo-core { fill: var(--primary, #0ea5c4); }
        .light .dc-logo-line { stroke: #5a6470; }
      `}</style>
      {/* Sitemark — crate silhouette that doubles as a site/location pin; the
          accent core follows the user's --primary so it tracks the chosen accent. */}
      <g transform="translate(1 0) scale(0.92)">
        <path
          d="M15 8 H33 A7 7 0 0 1 40 15 V26 L24 41 L8 26 V15 A7 7 0 0 1 15 8 Z"
          fill="none"
          className="dc-logo-line"
          strokeWidth="2.6"
          strokeLinejoin="round"
        />
        <circle cx="24" cy="21" r="4.8" className="dc-logo-core" />
      </g>
      <text x="54" y="30" fontFamily="Saira,sans-serif" fontSize="25" fontWeight="300" letterSpacing="3.4" fill="currentColor">DATACRATE</text>
    </svg>
  );
}
